import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { PaymentDto, PlatformSummary } from "@codestack/shared";
import { PAYMENT_STATUSES } from "@codestack/shared";
import { Button, Input, Select, StatusBadge } from "@codestack/ui";
import { downloadFile } from "../lib/api";
import { useApi } from "../lib/useApi";
import { EmptyState, ErrorState, LoadingState } from "../components/States";
import { dateTime, money, titleCase } from "../lib/format";

type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export function TransactionsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      page,
      pageSize: 20,
      status: status || undefined,
      platformId: platformId || undefined,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [page, status, platformId, search, from, to],
  );

  const { data, error, loading, reload } = useApi<Paginated<PaymentDto>>(
    "/admin/transactions",
    query,
  );
  const platforms = useApi<PlatformSummary[]>("/admin/platforms");

  const applySearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const resetFilters = () => {
    setStatus("");
    setPlatformId("");
    setSearch("");
    setSearchInput("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const hasFilters = Boolean(status || platformId || search || from || to);

  /**
   * Exports the current filter set — not just the visible page — so the file
   * matches what the operator has narrowed down to. Omitting `platformId`
   * exports every platform, which is the "all transactions" case.
   */
  const runExport = async (format: "csv" | "pdf") => {
    setExporting(format);
    setExportError(null);
    try {
      await downloadFile(
        "/admin/transactions/export",
        {
          format,
          platformId: platformId || undefined,
          status: status || undefined,
          search: search || undefined,
          from: from || undefined,
          to: to || undefined,
        },
        `codestack-transactions.${format}`,
      );
    } catch (cause) {
      setExportError(
        cause instanceof Error ? cause.message : "The export could not be generated.",
      );
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-xl font-bold text-white">Transactions</h1>
          <p className="text-sm text-ink-400">
            Every payment across all platforms, with provider verification state.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting !== null}
            onClick={() => void runExport("csv")}
          >
            {exporting === "csv" ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting !== null}
            onClick={() => void runExport("pdf")}
          >
            {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </Button>
        </div>
      </div>

      {exportError ? (
        <div className="admin-card border border-red-500/30 p-3 text-xs text-red-300">
          {exportError}
        </div>
      ) : null}

      <div className="admin-card p-4">
        <form onSubmit={applySearch} className="grid gap-3 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Input
              placeholder="Search reference, order or customer email"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div className="lg:col-span-3">
            <Select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {PAYMENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </Select>
          </div>
          <div className="lg:col-span-3">
            <Select
              value={platformId}
              onChange={(event) => {
                setPlatformId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All platforms</option>
              {(platforms.data ?? []).map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <Button type="submit" className="flex-1">
              Search
            </Button>
            {hasFilters ? (
              <Button type="button" variant="ghost" onClick={resetFilters}>
                Clear
              </Button>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:col-span-6">
            <Input
              label="From"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
            <Input
              label="To"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </form>
      </div>

      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {loading && !data ? <LoadingState label="Loading transactions…" /> : null}

      {data && data.items.length === 0 ? (
        <EmptyState
          title="No transactions match these filters"
          description="Try widening the date range or clearing the status filter."
          action={
            hasFilters ? (
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {data && data.items.length > 0 ? (
        <>
          <div className="table-shell">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Reference</th>
                    <th className="px-4 py-3 font-semibold">Platform</th>
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {data.items.map((payment) => (
                    <tr key={payment.id} className="transition-colors hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <Link
                          to={`/transactions/${payment.id}`}
                          className="font-mono text-[11px] text-ink-200 hover:text-brand-300"
                        >
                          {payment.reference}
                        </Link>
                        <p className="mt-0.5 font-mono text-[10px] text-ink-600">
                          {payment.orderReference}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-300">
                        {payment.platformName}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-400">
                        {payment.customerEmail ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-100">
                        {money(payment.amountMinor, payment.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={payment.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-500">
                        {dateTime(payment.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-xs text-ink-500">
              Showing {(data.page - 1) * data.pageSize + 1}–
              {Math.min(data.page * data.pageSize, data.total)} of {data.total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={data.page <= 1 || loading}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                Previous
              </Button>
              <span className="px-2 text-xs text-ink-400">
                Page {data.page} of {data.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={data.page >= data.totalPages || loading}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
